import { ChevronLeft, ChevronRight, Bell } from "lucide-react";
import {
  Box,
  keyframes,
} from "@mui/material";
import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { connect } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { environment } from "../../../environment";
import { StoreState } from "../../../models/reduxModels";
import {
  GetAllNotification,
  SeenNotificationPayload,
  userDetails,
} from "../../../models/userModels";
import {
  GetAllNotificationAction,
  resetPasswordAction,
  SeenNotificationAction,
} from "../../../stores/actions/userAction";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { getFinancialYear } from "../../../utility/checkFinYear";
import { GetMainDashboardAction } from "../../../stores/actions/organizationActions";
import {
  LayoutDashboard,
  Building2,
  Users,
  Fingerprint,
  UserCog,
  Circle,
} from "lucide-react";

const moduleIconMap: Record<string, React.ElementType> = {
  dashboard: LayoutDashboard,
  organization_management: Building2,
  employee_management: Users,
  attendance_management: Fingerprint,
  user_management: UserCog,
};

const Header = ({ user, logoutEvent, resetPasswordAction }: HeaderProps) => {
  const location = useLocation();
  const isOrangeTheme = ["dashboard", "organization", "employment", "attendance", "usermanagement"].includes(
    location.pathname.split("/")[1]?.toLowerCase()
  );
  const navigateTo = useNavigate();
  const [isAnimating, setIsAnimating] = useState(false);
  const [direction, setDirection] = useState<"left" | "right" | null>(null);
  const [currentPage, setCurrentPage] = useState(0);

  // Reset page to 0 when routing path changes to avoid out-of-bounds slice issues
  useEffect(() => {
    setCurrentPage(0);
  }, [location.pathname]);

  // Animation keyframes
  const slideOut = keyframes`
    0% { transform: translateX(0); opacity: 1; }
    100% { transform: translateX(${direction === "left" ? "-100%" : "100%"
    }); opacity: 0; }
  `;

  const slideIn = keyframes`
    0% { transform: translateX(${direction === "left" ? "100%" : "-100%"
    }); opacity: 0; }
    100% { transform: translateX(0); opacity: 1; }
  `;

  const navigate = useCallback(
    (item: any) => {
      if (item.has_submenu && item.menuItem.length > 0) {
        item.menuItem.every((element: any) => {
          if (
            user?.assess_control?.find(
              (m: any) => m.access_control_name === element.key
            )
          ) {
            navigateTo(element.url);
            return false;
          }
          return true;
        });
      } else {
        navigateTo(item.url);
      }
    },
    [user, navigateTo]
  );

  const assesCheck = useCallback(
    (element: any) => {
      let permission = false;
      if (element.has_submenu) {
        element.menuItem.forEach((m: any) => {
          if (
            user?.assess_control?.find(
              (n: any) => n.access_control_name === m.key && !m?.isnotmenu
            )
          ) {
            permission = true;
          }
        });
      } else {
        permission = !!user?.assess_control?.find(
          (m: any) => m.access_control_name === element.key
        );
      }
      return permission;
    },
    [user]
  );

  const visibleNavItems = useMemo(
    () => environment.sideNav.filter((item) => assesCheck(item)),
    [assesCheck]
  );

  const isActive = useCallback(
    (item: any) => {
      return location.pathname.split("/")[1] === item.url.split("/")[1];
    },
    [location.pathname]
  );

  const itemsPerPage = 6;
  const totalPages = Math.ceil(visibleNavItems.length / itemsPerPage);

  const scrollWithAnimation = useCallback(
    (newPage: number, dir: "left" | "right") => {
      if (isAnimating || newPage === currentPage) return;

      setDirection(dir);
      setIsAnimating(true);

      setTimeout(() => {
        setCurrentPage(newPage);
        setIsAnimating(false);
      }, 300);
    },
    [isAnimating, currentPage]
  );

  const scrollLeft = useCallback(() => {
    const newPage = Math.max(0, currentPage - 1);
    scrollWithAnimation(newPage, "right");
  }, [currentPage, scrollWithAnimation]);

  const scrollRight = useCallback(() => {
    const newPage = Math.min(totalPages - 1, currentPage + 1);
    scrollWithAnimation(newPage, "left");
  }, [currentPage, totalPages, scrollWithAnimation]);

  const visibleNavItemsMenu = useMemo(
    () =>
      visibleNavItems.slice(
        currentPage * itemsPerPage,
        (currentPage + 1) * itemsPerPage
      ),
    [visibleNavItems, currentPage]
  );

  const dispatch = useDispatch();
  const notificationRef = useRef<HTMLDivElement>(null);
  const notificationWrapperRef = useRef<HTMLDivElement>(null);
  const today = new Date();
  const currentFY = getFinancialYear(today);
  const [showNotifications, setShowNotifications] = useState(false);
  const [viewAll, setViewAll] = useState(false);
  const get_all_notification = useSelector(
    (state: StoreState) => state.user.get_all_notification
  );
  const unreadCount =
    get_all_notification &&
    get_all_notification.length > 0 &&
    get_all_notification.filter((n) => n.seen === 0).length;
  const handleShowNotification = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (showNotifications) {
      setShowNotifications(false);
      setViewAll(false);
    } else {
      dispatch(GetAllNotificationAction() as any);
      setShowNotifications(true);
    }
  };

  const unseenNotiCount = useSelector(
    (state: StoreState) => state.organization.unseenNotiCount
  );
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        notificationWrapperRef.current &&
        !notificationWrapperRef.current.contains(event.target as Node)
      ) {
        setShowNotifications(false);
        setViewAll(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const successCallBackSeenNotification = (data: any) => {
    dispatch(
      GetMainDashboardAction({
        employee_type_id: null,
        year: currentFY.yearLabel,
      }) as any
    );
    setShowNotifications(false);
  };
  const handleSeenNotificationViewDetails = (data: any) => {
    switch (data && data.notification_type) {
      case 1:
        return navigateTo("/employment/onboarding-request-app-rej/");
      case 2:
        return navigateTo("/leave/leave-approval/");
      case 3:
        return navigateTo("/leave/employee-leave-application/");
      case 4:
        return navigateTo(
          `/employment/manage-profile/${user && user.employeeId}`
        );
      case 5:
        return navigateTo("/attendance/attendence-pending-approval/");
      case 6:
        return navigateTo("/attendance/my-attendence/");
      case 7:
        return navigateTo("/payroll/pending-approval/");
      case 8:
        return navigateTo("/promotion/approve-promotion/");
      case 9:
        return navigateTo(
          `/employment/manage-profile/${user && user.employeeId}`
        );
      case 10:
        return navigateTo(`/termination/employee_list_for_termination/`);
      case 11:
        return navigateTo("/expence/pending-expence/");
      case 12:
        return navigateTo("/expence/employee-expence/");
      case 13:
        return navigateTo("/timingsheet/timing-sheet-approve/");
      case 14:
        return navigateTo("/timingsheet/log-activity/");
      default:
        break;
    }
  };
  const handleSeenNotification = (data: any) => {
    let payload: SeenNotificationPayload = {
      notificationId: data.notification_id,
    };
    dispatch(
      SeenNotificationAction({
        payload: payload,
        successCallBack: successCallBackSeenNotification,
      }) as any
    );
    handleSeenNotificationViewDetails(data);
  };

  return (
    <Box
      sx={{
        height: 64,
        backgroundColor: "var(--kinetic-header-bg)",
        borderBottom: "1px solid var(--kinetic-header-border)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 2,
        position: "relative",
      }}
    >
      <div className="flex items-center flex-1 h-full overflow-hidden">
        <button
          onClick={scrollLeft}
          disabled={currentPage === 0 || isAnimating}
          className="p-1 rounded-md mr-2 text-onSurfaceVariant hover:bg-background disabled:opacity-40 transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            position: "relative",
            height: isOrangeTheme ? "100%" : 48,
          }}
        >
          <div
            className="absolute w-full h-full flex justify-center items-center"
            style={{
              animation: isAnimating
                ? `${direction === "left" ? slideIn : slideOut} 0.3s ease-out forwards`
                : "none",
            }}
          >
            {visibleNavItemsMenu.map((item: any, index: number) => {
              const active = isActive(item);
              const IconComponent = moduleIconMap[item.key] || Circle;
              if (isOrangeTheme) {
                return (
                  <button
                    key={`${item.id || index}-${currentPage}`}
                    onClick={() => navigate(item)}
                    className={`flex flex-col items-center justify-center h-full px-4 min-w-[96px] transition-all relative border-b-2 ${
                      active
                        ? "text-primary border-primary font-semibold"
                        : "text-onSurfaceVariant border-transparent hover:text-primary"
                    }`}
                  >
                    <div className="flex items-center justify-center w-8 h-8 mb-0.5">
                      <IconComponent className={`w-5 h-5 ${active ? "text-primary" : "text-gray-400"}`} />
                    </div>
                    <span className="text-[11px] font-medium text-center whitespace-nowrap overflow-hidden text-ellipsis max-w-[120px]">
                      {item.name}
                    </span>
                  </button>
                );
              } else {
                return (
                  <button
                    key={`${item.id || index}-${currentPage}`}
                    onClick={() => navigate(item)}
                    className={`flex flex-col items-center justify-center h-12 px-3 min-w-[96px] transition-colors rounded-md ${active
                        ? "text-primary"
                        : "text-onSurfaceVariant hover:text-onSurface hover:bg-background"
                      }`}
                  >
                    <div
                      className={`flex items-center justify-center w-8 h-8 rounded-full mb-0.5 ${active ? "bg-primaryLight text-primary" : ""
                        }`}
                    >
                      <IconComponent className="w-5 h-5" />
                    </div>
                    <span
                      className={`text-[11px] font-medium text-center whitespace-nowrap overflow-hidden text-ellipsis max-w-[120px] ${active ? "font-semibold" : ""
                        }`}
                    >
                      {item.name}
                    </span>
                    {active && (
                      <span className="absolute bottom-1 w-5 h-0.5 rounded-full bg-primary" />
                    )}
                  </button>
                );
              }
            })}
          </div>
        </Box>

        <button
          onClick={scrollRight}
          disabled={currentPage >= totalPages - 1 || isAnimating}
          className="p-1 rounded-md ml-2 text-onSurfaceVariant hover:bg-background disabled:opacity-40 transition-colors"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      <div
        ref={notificationWrapperRef}
        className="flex items-center space-x-4"
        style={{ zIndex: 999 }}
      >
        <div className="relative">
          <button
            onClick={handleShowNotification}
            className="relative p-2 rounded-lg text-onSurfaceVariant hover:bg-background hover:text-primary transition-all duration-200 group"
          >
            <Bell className="w-6 h-6 group-hover:scale-110 transition-transform duration-200" />
            {unseenNotiCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-primary text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                {unseenNotiCount}
              </span>
            )}
          </button>
          {showNotifications && (
            <div
              ref={notificationRef}
              className="absolute right-0 top-full mt-2 w-80 bg-surface rounded-lg border border-outline shadow-dropdown z-50 overflow-hidden"
            >
              <div className="p-3 border-b border-divider">
                <h3 className="font-semibold text-onSurface">Notifications</h3>
                <p className="text-sm text-onSurfaceVariant">
                  {unreadCount} unread notifications
                </p>
              </div>
              <div
                className="overflow-y-auto transition-[max-height] duration-700 ease-in-out"
                style={{ maxHeight: viewAll ? "60vh" : "195px" }}
              >
                {(get_all_notification || [])
                  .slice(0, viewAll ? get_all_notification.length : 3)
                  .map((notification: GetAllNotification) => (
                    <div
                      key={notification.notification_id}
                      className={`p-3 border-b border-divider hover:bg-background transition-colors cursor-pointer ${notification.seen === 0 ? "bg-surfaceLow" : ""}`}
                    >
                      <div className="flex items-start space-x-3">
                        <div
                          className={`w-2 h-2 rounded-full mt-2 ${notification.seen === 0 ? "bg-primary" : "bg-divider"}`}
                        ></div>
                        <div className="flex-1">
                          <p
                            onClick={() => handleSeenNotification(notification)}
                            className="text-sm font-medium text-onSurface hover:underline transition"
                          >
                            {notification.title}
                          </p>

                          {notification.short_desc && (
                            <p className="text-xs text-onSurfaceVariant mt-1">
                              {notification.short_desc.length > 255
                                ? `${notification.short_desc.slice(0, 255)}...`
                                : notification.short_desc}
                            </p>
                          )}

                          <p className="text-xs text-onSurfaceVariant mt-1">
                            {moment(notification.created_on).format(
                              "DD MMM YYYY, hh:mm A"
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>

              <div className="p-3 border-t border-divider">
                <button
                  onClick={() => setViewAll(!viewAll)}
                  className="w-full text-center text-sm text-primary hover:text-primaryHover font-medium"
                >
                  {viewAll ? "View less" : "View all notifications"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Box>
  );
};

const mapStateToProps = (state: StoreState) => {
  return {
    user: state.user.userDetails,
  };
};
const mapDispatchToProps = { resetPasswordAction };

export default connect(mapStateToProps, mapDispatchToProps)(Header);

interface HeaderProps {
  user?: userDetails;
  logoutEvent?: any;
  resetPasswordAction?: any;
}
